import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useSiteContent } from "@/context/SiteContentContext";
import { CURATED_KEYS as K } from "@/components/CuratedDropSection";

const compressLogo = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onerror = reject;
  r.onload = () => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, 480 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/png"));
    };
    img.src = r.result;
  };
  r.readAsDataURL(file);
});

export default function AdminCuratedDropCard() {
  const { content, refresh } = useSiteContent();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (form || !Object.keys(content).length) return;
    setForm({
      visible: content[K.visible] !== "false",
      label: content[K.label] || "", heading: content[K.heading] || "", description: content[K.description] || "",
      logo: content[K.logo] || "", vendorText: content[K.vendorText] || "",
      logoPosition: content[K.logoPosition] === "right" ? "right" : "left",
      logoSize: Number(content[K.logoSize]) || 96,
    });
  }, [content, form]);

  if (!form) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async (patch = {}) => {
    const next = { ...form, ...patch };
    setBusy(true);
    try {
      const pairs = [[K.visible, next.visible ? "true" : "false"], [K.label, next.label], [K.heading, next.heading],
        [K.description, next.description], [K.logo, next.logo], [K.vendorText, next.vendorText],
        [K.logoPosition, next.logoPosition], [K.logoSize, String(next.logoSize)]];
      for (const [key, value] of pairs) await api.patch(`/admin/site-content/${key}`, { value });
      setForm(next);
      await refresh();
      toast.success("Curated Drop section saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const onLogo = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    try { set({ logo: await compressLogo(f) }); } catch { toast.error("Could not read this image"); }
  };

  return (
    <div className="mb-8 p-5 bg-white border border-gray-200 rounded-2xl" data-testid="admin-curated-drop-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-lg">Today's Curated Drop section</div>
          <div className="text-xs text-gray-500">Homepage block below the products. Empty fields are not shown.</div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid="curated-visible-toggle" checked={form.visible} onCheckedChange={(v) => save({ visible: v })} disabled={busy} />
          {form.visible ? "Shown on website" : "Hidden"}
        </label>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div><Label>Label</Label><Input data-testid="curated-label-input" value={form.label} onChange={(e) => set({ label: e.target.value })} /></div>
          <div><Label>Heading</Label><Input data-testid="curated-heading-input" value={form.heading} onChange={(e) => set({ heading: e.target.value })} /></div>
          <div><Label>Description</Label><Textarea data-testid="curated-description-input" rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} /></div>
          <div><Label>Exhibitor description</Label><Textarea data-testid="curated-vendor-text-input" rows={3} placeholder="Short note about the featured exhibitor" value={form.vendorText} onChange={(e) => set({ vendorText: e.target.value })} />
            {form.vendorText && <button type="button" data-testid="curated-vendor-text-delete" onClick={() => set({ vendorText: "" })} className="mt-1 text-xs text-red-500">Delete exhibitor description</button>}
          </div>
        </div>
        <div className="space-y-3">
          <Label>Exhibitor logo</Label>
          <div className="flex items-center gap-3">
            {form.logo
              ? <img data-testid="curated-logo-preview" src={form.logo} alt="Logo preview" className="h-20 w-20 object-contain rounded-xl border border-gray-200" />
              : <div className="h-20 w-20 rounded-xl border border-dashed border-gray-300 text-[10px] text-gray-400 flex items-center justify-center">No logo</div>}
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-dashed border-[#F48E2E] text-[#F48E2E] text-xs hover:bg-[#FEF3E6]">
                <Upload className="h-3.5 w-3.5" /> {form.logo ? "Replace logo" : "Upload logo"}
                <input data-testid="curated-logo-upload" type="file" accept="image/*" hidden onChange={onLogo} />
              </label>
              {form.logo && <button type="button" data-testid="curated-logo-delete" onClick={() => set({ logo: "" })} className="inline-flex items-center gap-1 text-xs text-red-500"><Trash2 className="h-3 w-3" /> Delete logo</button>}
            </div>
          </div>
          <div>
            <Label className="text-xs">Logo position (desktop)</Label>
            <div className="flex gap-2 mt-1">
              {["left", "right"].map((p) => (
                <Button key={p} type="button" data-testid={`curated-logo-pos-${p}`} variant={form.logoPosition === p ? "default" : "outline"} onClick={() => set({ logoPosition: p })} className="rounded-full h-8 text-xs capitalize">{p}</Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Logo size: {form.logoSize}px</Label>
            <input data-testid="curated-logo-size" type="range" min="40" max="240" step="4" value={form.logoSize} onChange={(e) => set({ logoSize: Number(e.target.value) })} className="w-full accent-[#F48E2E]" />
          </div>
        </div>
      </div>
      <Button data-testid="curated-save" onClick={() => save()} disabled={busy} className="mt-4 rounded-full bg-[#F48E2E] hover:bg-[#D97815]">{busy ? "Saving…" : "Save section"}</Button>
    </div>
  );
}
